import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Stethoscope, Scales, ChartLineUp, Briefcase, ArrowRight, Check } from "@phosphor-icons/react";
import { Reveal } from "@/components/site/Motion";
import { useDiagnostic } from "@/context/DiagnosticContext";

const ICONS = { Stethoscope, Scales, ChartLineUp, Briefcase };

export default function SectorSelector({ content }) {
  const s = content?.sectors || {};
  const items = s.items || [];
  const [active, setActive] = useState(0);
  const { openDiagnostic } = useDiagnostic();
  if (!items.length) return null;
  const current = items[active] || items[0];

  return (
    <section id="sectores" className="px-6 md:px-12 lg:px-24 py-24 md:py-32 border-t border-border">
      <Reveal>
        <h4 className="font-sans text-sm tracking-[0.2em] uppercase text-faint mb-4">Selector de sector</h4>
        <h2 className="font-serif text-4xl md:text-6xl tracking-tight text-ink max-w-3xl leading-[1.05]">{s.heading}</h2>
        <p className="font-sans text-base md:text-lg text-subtle mt-6 max-w-xl">{s.subtitle}</p>
      </Reveal>

      <div className="mt-14 grid lg:grid-cols-12 gap-8 lg:gap-12">
        <div className="lg:col-span-4 flex flex-col gap-3">
          {items.map((it, i) => {
            const Icon = ICONS[it.icon] || Briefcase;
            const on = i === active;
            return (
              <button
                key={it.key}
                data-testid={`sector-tab-${it.key}`}
                onClick={() => setActive(i)}
                className={`group flex items-center gap-4 text-left px-5 py-5 rounded-xl border transition-[background-color,border-color] duration-300 ${
                  on ? "bg-ink border-ink" : "bg-white border-border hover:border-ink/40"
                }`}
              >
                <span className={`shrink-0 w-11 h-11 rounded-full flex items-center justify-center transition-colors duration-300 ${on ? "bg-white/10" : "bg-stone-100"}`}>
                  <Icon size={22} weight="light" className={on ? "text-stone-50" : "text-ink"} />
                </span>
                <span className="flex-1">
                  <span className={`block font-serif text-xl leading-none ${on ? "text-stone-50" : "text-ink"}`}>{it.label}</span>
                </span>
                <ArrowRight size={18} className={`transition-transform duration-300 ${on ? "text-stone-50 translate-x-0" : "text-faint -translate-x-1 group-hover:translate-x-0"}`} />
              </button>
            );
          })}
        </div>

        <div className="lg:col-span-8">
          <AnimatePresence mode="wait">
            <motion.div
              key={current.key}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              className="grid md:grid-cols-2 gap-8 items-stretch"
            >
              <div className="overflow-hidden rounded-2xl bg-stone-100 aspect-[4/3] md:aspect-auto md:min-h-[340px]">
                <img src={current.image} alt={current.label} className="w-full h-full object-cover" />
              </div>
              <div className="flex flex-col justify-center py-2">
                <h3 className="font-serif text-2xl md:text-3xl text-ink leading-tight">{current.headline}</h3>
                <ul className="mt-6 space-y-4">
                  {current.applications?.map((a, k) => (
                    <li key={k} data-testid={`sector-app-${current.key}-${k}`} className="flex items-start gap-3 font-sans text-sm md:text-base text-subtle">
                      <span className="mt-1 shrink-0 w-5 h-5 rounded-full bg-stone-200 flex items-center justify-center"><Check size={12} weight="bold" className="text-ink" /></span>
                      {a}
                    </li>
                  ))}
                </ul>
                <button
                  data-testid={`sector-cta-${current.key}`}
                  onClick={() => openDiagnostic(current.key)}
                  className="group mt-8 inline-flex items-center gap-2 font-sans text-sm text-ink border-b border-ink/20 pb-1 hover:border-ink transition-colors duration-300 w-fit"
                >
                  Solicitar diagnóstico para {current.label}
                  <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform duration-300" />
                </button>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}
